import LongTailLanding from "./LongTailLanding";
import SeoLandingPage from "./SeoLandingPage";

// === Editorial SEO landing pages — created per Iteration 22 brief ===
// Each page uses SeoLandingPage (the new editorial template) and matches
// the brand visual system. Older long-tail pages below continue to use
// LongTailLanding for backwards compatibility.

export const DesignLedSouthDelhi = () => (
  <SeoLandingPage
    pageId="design-led-south-delhi"
    title="Design-Led Stays in South Delhi · Vyagram Experiences"
    metaDescription="A portfolio of design-led, professionally hosted stays across South Delhi — Dhan Mill, Greater Kailash, Hauz Khas. Direct booking, on-ground hospitality."
    canonicalPath="/design-led-stays-south-delhi"
    eyebrow="South Delhi · Design portfolio"
    heading="Design-led stays across South Delhi —"
    scriptHeading="quietly considered, end to end."
    intro="Every Vyagram home in South Delhi is built and hosted by us — not franchised, not templated. Cinematic 2BHKs near Dhan Mill, dark-luxe studios in GK 1, and view-led residences with the Qutub on the horizon. Direct rates, hotel-grade essentials, and a hospitality team you can actually reach."
    body={[
      { title: "Built for the way you actually stay", body: "Whether it's a quiet weekend, a longer work reset or an evening with a small circle, every home is laid out for the moment — not the photo." },
      { title: "Hosted by us, end to end", body: "Housekeeping, check-in, late-night fixes, stocking the fridge — handled by our on-ground team, not a freelancer answering a property manager." },
    ]}
    filter={{ location: "south-delhi" }}
    ctaCopy={{ subtitle: "Tell us a little about your trip — we'll come back with the right South Delhi home for you." }}
  />
);

export const DirectBookingDelhiNCR = () => (
  <SeoLandingPage
    pageId="direct-booking-delhi-ncr"
    title="Direct Booking Stays in Delhi NCR · Vyagram"
    metaDescription="Book design-led, professionally hosted homes across Delhi NCR directly — no OTA markups, real hospitality, transparent rates."
    canonicalPath="/direct-booking-delhi-ncr"
    eyebrow="Direct booking"
    heading="Skip the markup."
    scriptHeading="Book directly with us."
    intro="Direct booking with Vyagram means the same homes, the same standards — but at our real rate, not the OTA-inflated one. Same-day availability, instant WhatsApp answers, and a bookable confirmation in minutes."
    body={[
      { title: "Why direct", body: "OTAs charge 12–18% on top of the host. We pass that saving directly to guests who book with us — and we put the relationship first, not the platform algorithm." },
      { title: "How it works", body: "Pick your dates on the property page. We confirm live availability against our PMS. Pay a small reservation amount; the rest at check-in. No hidden inflation, no platform fees." },
    ]}
    filter={{}}
    ctaCopy={{ subtitle: "Direct rates, real hospitality. Tell us where you'd like to stay." }}
  />
);

export const AirbnbAlternativesSouthDelhi = () => (
  <SeoLandingPage
    pageId="airbnb-alternatives-south-delhi"
    title="Airbnb Alternatives in South Delhi · Vyagram"
    metaDescription="Looking beyond Airbnb? Vyagram is a managed, design-led alternative across South Delhi — direct booking, dedicated hospitality, no OTA fees."
    canonicalPath="/airbnb-alternatives-south-delhi"
    eyebrow="A different kind of stay"
    heading="An Airbnb alternative —"
    scriptHeading="without the Airbnb trade-offs."
    intro="Variable interiors, OTA fees, host availability that depends on the day — the Airbnb experience can be uneven. Vyagram is built differently: every home is ours, hosted end-to-end by the same team, with consistent design and service guests can count on."
    body={[
      { title: "Same homes you'd find", body: "You can find Vyagram listings on Airbnb too. Booking direct gets you the same homes at our real rate, with quicker answers and no OTA mediation." },
      { title: "On-ground, not online", body: "Hospitality means a real person at the door, on WhatsApp, and on-call through your stay — not a checklist forwarded by a property manager." },
    ]}
    filter={{ location: "south-delhi" }}
    ctaCopy={{ subtitle: "Want the same home at a better rate? Plan your stay with us directly." }}
  />
);

export const PrivateStayCouplesDelhiNCR = () => (
  <SeoLandingPage
    pageId="private-stay-couples-delhi-ncr"
    title="Private Stays for Couples in Delhi NCR · Vyagram"
    metaDescription="Quiet, private, design-led stays for couples across South Delhi and Gurugram. Anniversaries, weekends, slow getaways — handled discreetly."
    canonicalPath="/private-stay-couples-delhi-ncr"
    eyebrow="Couples · Private stays"
    heading="A private corner —"
    scriptHeading="just for the two of you."
    intro="Quiet corners, soft light, and private spaces made for two. Our couples stays are calm, dark-luxe homes with king beds, ensuite baths, blackout curtains, and kitchens you can actually use. Hosted by us, with privacy as the default."
    body={[
      { title: "Anniversaries & getaways", body: "Speakeasy, Ivory, Noir and Lune are some of our most-loved couples stays — each one set up so you can disappear for the weekend without lifting a finger." },
      { title: "Discreet hospitality", body: "We greet you at check-in and step back. Daily housekeeping is timed around your day, never on top of it." },
    ]}
    filter={{ collection: "couples" }}
    ctaCopy={{ subtitle: "Tell us your dates — we'll pick the right private home for the two of you." }}
  />
);

export const ShootFriendlyStaysDelhiNCR = () => (
  <SeoLandingPage
    pageId="shoot-friendly-stays-delhi-ncr"
    title="Shoot-Friendly Stays in Delhi NCR · Vyagram"
    metaDescription="Design-first homes in South Delhi and Gurugram for editorial shoots, music videos, brand films and content creation. Permits, props, rates handled."
    canonicalPath="/shoot-friendly-stays-delhi-ncr"
    eyebrow="Creators & commercial shoots"
    heading="Ten homes, "
    scriptHeading="one production address."
    intro="Vyagram's portfolio doubles as a working location library — Japandi, cinematic noir, gothic speakeasy, illustrated maximalist, cyberpunk, and more. Each home is architect-built, with parking, power and on-ground production support included."
    body={[
      { title: "Built for the frame", body: "Every property is shot-ready — interiors that read on camera, lighting we've already tested, and corners that don't need set dressing to land an editorial." },
      { title: "End-to-end production support", body: "Our team handles location permits, on-site coordination and security so the day on set is yours to focus on. Half-day, full-day and multi-day rates available." },
    ]}
    filter={{}}
    ctaCopy={{ subtitle: "Send us your brief — we'll come back with the right home and a tailored rate." }}
  />
);

export const SouthDelhiStays = () => (
  <LongTailLanding
    slug="south-delhi-stays"
    title="Luxury Stays in South Delhi"
    subtitle="A curated portfolio of design-led homes near Dhan Mill, GK 1 and the Qutub zone."
    description="Our South Delhi stays sit in the quieter pockets of the city — close to the best cafés, design stores and cultural corners, while staying private and easy to return to. Book direct for best rates, on-ground hospitality, and real people answering real questions."
    keywords="luxury stay south delhi, design stay south delhi, airbnb alternative south delhi, direct booking"
    filter={(p) => p.location_slug === "south-delhi"}
  />
);

export const GK1Stays = () => (
  <LongTailLanding
    slug="gk1-stays"
    title="Stays in Greater Kailash 1"
    subtitle="Private, design-first homes in one of Delhi's most central neighbourhoods."
    description="Greater Kailash 1 puts you steps from M-Block Market, minutes from the metro and surrounded by some of the city's best restaurants, cafés and boutiques. Noir and Ivory are our GK 1 residences — quiet, tonal, and made for couples or work trips."
    keywords="stay GK 1, greater kailash 1 airbnb alternative, GK 1 luxury stay"
    filter={(p) => p.location_slug === "gk1"}
  />
);

export const GurgaonStays = () => (
  <LongTailLanding
    slug="gurgaon-stays"
    title="Luxury Stays in Gurugram"
    subtitle="Quiet-luxe homes in Uppal Southend — leafy, calm and connected."
    description="Gurugram after hours deserves a calmer kind of home. Sombre sits on a quiet, green lane in Uppal Southend with tree views, a Japandi-tuned interior and step-free access — designed for families, long stays and slower weeks."
    keywords="luxury stay gurgaon, airbnb alternative gurugram, uppal southend stay"
    filter={(p) => p.location_slug === "gurugram"}
  />
);

export const NearDhanMill = () => (
  <LongTailLanding
    slug="near-dhan-mill"
    title="Stays Near Dhan Mill Compound"
    subtitle="Walking distance to Delhi's best design-led district — cafes, ateliers and quiet corners."
    description="Dhan Mill Compound is the centre of Delhi's design energy — a walkable grid of cafés, studios and concept stores. Our stays here (Fête, Doodle, Lune, Cyberpunk, Vista de Qutub, Cinéma, Speakeasy) put you steps from all of it."
    keywords="stay near dhan mill, dhan mill compound hotel, design stay near dhan mill"
    filter={(p) => (p.location_label || "").toLowerCase().includes("dhan mill")}
  />
);

export const LuxuryStaysDelhi = () => (
  <LongTailLanding
    slug="luxury-stays-delhi"
    title="Luxury Stays in Delhi"
    subtitle="Editorial homes. Direct booking. Real hospitality."
    description="Every Vyagram home is designed and run by us — not franchised, not templated. You get curated interiors, premium hotel-grade essentials, and a dedicated on-ground team that's reachable throughout your stay. Anywhere between one night and one month, this is luxury without the OTA markups."
    keywords="luxury stay delhi, premium airbnb delhi, boutique stay delhi"
  />
);

export const RomanticStaysDelhi = () => (
  <LongTailLanding
    slug="romantic-stays-delhi"
    title="Romantic Stays in Delhi"
    subtitle="Dark-luxe speakeasy suites and cinematic apartments for two."
    description="Intimate, quiet, and carefully lit — our couples stays are designed for slow conversations and long evenings. Think moody palettes, Dolby Atmos, 120&rdquo; projectors, king beds with blackouts, and service that arrives only when invited."
    keywords="romantic stay delhi, couples airbnb delhi, anniversary stay delhi"
    filter={(p) => p.collections?.includes("couples")}
  />
);

export const CinematicStaysDelhi = () => (
  <LongTailLanding
    slug="cinematic-stays-delhi"
    title="Cinematic & Home-Theatre Stays"
    subtitle="Design-forward apartments made for movie nights, gaming, and shoots."
    description="A 100&rdquo; projector, a 400W Atmos system, neon gaming rooms, magnetic lighting rails and walls that carry the mood from room to room. These aren't themed by room — they're whole-home experiences."
    keywords="home theatre stay delhi, projector airbnb, cinematic stay delhi, gaming stay"
    filter={(p) => p.collections?.includes("cinematic")}
  />
);

export const GroupStaysDelhi = () => (
  <LongTailLanding
    slug="group-stays-delhi"
    title="Group Stays in Delhi"
    subtitle="3 & 4 BHK homes with ensuite baths, full kitchens and dedicated hospitality."
    description="Room for the whole group without compromising on design or service. Our group-ready stays include double living rooms, extra-large king beds, full modular kitchens and daily housekeeping that works around your day."
    keywords="group stay delhi, 4bhk airbnb delhi, family stay delhi"
    filter={(p) => p.collections?.includes("group-getaways")}
  />
);

export const ExtendedStaysDelhi = () => (
  <LongTailLanding
    slug="extended-stays-delhi"
    title="Long Stays & Workations in Delhi"
    subtitle="Move in, slow down, stay a while."
    description="Fully equipped kitchens, 200 Mbps Wi-Fi, ergonomic work corners, daily housekeeping and inverter-backed essentials. Our long-stay properties hold up beautifully across weeks — and get better the longer you're there."
    keywords="long stay delhi, workation delhi, monthly rental delhi, extended stay delhi"
    filter={(p) => p.collections?.includes("long-stays")}
  />
);

export const ExperientialDesignStays = () => (
  <LongTailLanding
    slug="experiential-design-stays"
    title="Experiential Design Stays in Delhi"
    subtitle="Sombre · Cinéma · Speakeasy · Doodle · Cyberpunk · Noir — homes with a point of view."
    description="Each Experiential Getaway is a single-concept home: a whole-property aesthetic rather than a themed room. Our Japandi apartment (Sombre), neon cyber loft (Cyberpunk), gothic speakeasy (Speakeasy), arthouse cinema (Cinéma), illustrated maximalist (Doodle) and tonal noir studio (Noir) are each built to be lived in — not just photographed. Book direct for best rates."
    keywords="experiential stay delhi, design airbnb delhi, themed stay delhi, cyberpunk airbnb, japandi stay delhi, speakeasy apartment delhi"
    filter={(p) => p.collections?.includes("experiential-design")}
  />
);
